/**
 * src/api/planApi.js
 *
 * Frontend API for plan definitions and Shieldwall program config.
 * Read is public (needed for plan enforcement on tenant side + pricing page).
 * Write requires super-admin auth (handled by azureClient JWT attachment).
 */

import { azureGet, azurePost } from './azureClient.js';
import { DEFAULT_PLAN_DEFINITIONS, DEFAULT_SHIELDWALL } from '../config/planDefaults.js';

/**
 * Load plan definitions from the server.
 * Falls back to hardcoded defaults only when the server responds successfully
 * with none saved yet — a failed request throws so the caller can show a
 * real failure/retry state instead of silently rendering fabricated pricing
 * and plan-gating data as if it were live.
 *
 * `loadPlanDefinitionsResult` is the same call with the one fact this return
 * shape cannot carry: whether the definitions came FROM the server or were
 * substituted here. A 200 with no `.definitions` and a 200 with real
 * definitions are indistinguishable once the `??` has run, and #5180 is
 * exactly that — a tenant whose plan the server never confirmed being treated
 * client-side as a confirmed standard plan.
 */
export async function loadPlanDefinitions() {
  const { definitions } = await loadPlanDefinitionsResult();
  return definitions;
}

/**
 * @returns {Promise<{ definitions: object, fromServer: boolean }>}
 *   fromServer: false means the server answered successfully but had no plan
 *   definitions stored, so what you are holding is this app's own defaults —
 *   a reasonable thing to render, never a statement about this tenant's plan.
 */
export async function loadPlanDefinitionsResult() {
  const data = await azureGet('/api/plan-definitions');
  return {
    definitions: data.definitions ?? DEFAULT_PLAN_DEFINITIONS,
    fromServer: data.definitions != null,
  };
}

/**
 * Load Shieldwall program config from the server.
 * Falls back to hardcoded defaults only when the server responds successfully
 * with none saved yet — see loadPlanDefinitions for why request failures throw.
 */
export async function loadShieldwall() {
  const data = await azureGet('/api/plan-definitions');
  return data.shieldwall ?? DEFAULT_SHIELDWALL;
}

/**
 * Load both plan definitions and shieldwall config in a single request.
 * See loadPlanDefinitions for why request failures throw instead of
 * returning hardcoded defaults.
 */
export async function loadPlanData() {
  const data = await azureGet('/api/plan-definitions');
  return {
    definitions: data.definitions ?? DEFAULT_PLAN_DEFINITIONS,
    shieldwall: data.shieldwall ?? DEFAULT_SHIELDWALL,
    definitionsFromServer: data.definitions != null,
    shieldwallFromServer: data.shieldwall != null,
  };
}

/**
 * Save plan definitions (super-admin only).
 */
export async function savePlanDefinitions(definitions) {
  await azurePost('/api/plan-definitions', { definitions });
}

/**
 * Save Shieldwall program config (super-admin only).
 */
export async function saveShieldwall(shieldwall) {
  await azurePost('/api/plan-definitions', { shieldwall });
}
